import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Play, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageProvider";
import { genderFromAvatarUrl, isOpenAiVoice, OPENAI_VOICE_GENDER } from "@/lib/openaiVoices";
import { VOICE_LIBRARY, getVoicesByGender, type VoiceGender } from "@/lib/voiceLibrary";

type RowState = "idle" | "loading-preview" | "saving";

const SettingsVoice = () => {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const [avatarGender, setAvatarGender] = useState<VoiceGender | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [rowState, setRowState] = useState<Record<string, RowState>>({});
  const isSaving = Object.values(rowState).includes("saving");
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const previewRequest = useRef(0);
  const savingRef = useRef(false);

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/auth?mode=signin", { replace: true }); return; }
      const { data, error } = await supabase
        .from("user_profiles")
        .select("voice_id_selected, sex, avatar_url")
        .eq("user_id", user.id)
        .maybeSingle();
      if (error || !data) {
        toast.error(t("common.error"));
        setLoading(false);
        return;
      }
      // Derive avatar gender same way edge fn does.
      const url = (data.avatar_url as string | null) ?? null;
      const g = genderFromAvatarUrl(url);
      setAvatarGender(g);
      setSelectedId(isOpenAiVoice(data.voice_id_selected) &&
        (!g || OPENAI_VOICE_GENDER[data.voice_id_selected] === g) ? data.voice_id_selected : null);
      setLoading(false);
    })();
    return () => {
      previewRequest.current++;
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    };
  }, [navigate, t]);

  const handlePreview = async (voiceId: string) => {
    const request = ++previewRequest.current;
    setRowState((s) => Object.fromEntries(Object.entries(s).map(([key, value]) => [key, value === "loading-preview" ? "idle" : value])));
    setRowState((s) => ({ ...s, [voiceId]: "loading-preview" }));
    try {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      const { data, error } = await supabase.functions.invoke("mirror-tts", {
        body: {
          text: t("settings.voice.sample"),
          voice: voiceId,
          voice_override_explicit: true,
          language: lang,
          format: "url",
        },
      });
      if (request !== previewRequest.current) return;
      if (error || !data?.url) throw new Error(error?.message ?? "no url");
      const audio = new Audio(data.url as string);
      audioRef.current = audio;
      audio.onended = () => setRowState((s) => ({ ...s, [voiceId]: "idle" }));
      audio.onerror = () => setRowState((s) => ({ ...s, [voiceId]: "idle" }));
      await audio.play();
    } catch (err) {
      if (request !== previewRequest.current) return;
      console.error("[SettingsVoice] preview failed:", err);
      toast.error(t("common.error"));
      setRowState((s) => ({ ...s, [voiceId]: "idle" }));
    }
  };

  const handleSelect = async (voiceId: string | null) => {
    if (savingRef.current) return;
    savingRef.current = true;
    const key = voiceId ?? "__default__";
    setRowState((s) => ({ ...s, [key]: "saving" }));
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data, error } = await supabase.from("user_profiles")
        .update({ voice_id_selected: voiceId, voice_pref: "preset" })
        .eq("user_id", user.id).select("voice_id_selected").maybeSingle();
      if (error || !data) throw error ?? new Error("Voice preference was not saved");
      setSelectedId(data.voice_id_selected);
      toast.success(t("settings.voice.savedToast"));
    } catch (error) {
      console.error("[SettingsVoice] update failed:", error);
      toast.error(t("common.error"));
    } finally {
      savingRef.current = false;
      setRowState((s) => ({ ...s, [key]: "idle" }));
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "hsl(222 38% 6%)" }}>
      <div
        className="flex items-center gap-3 px-5 pb-4"
        style={{ paddingTop: "max(env(safe-area-inset-top, 0px) + 1rem, 3.5rem)" }}
      >
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[13px] text-foreground/60 hover:text-foreground transition-colors"
        >
          <ChevronLeft size={16} />
          {t("common.back")}
        </button>
        <h1 className="flex-1 text-center text-[15px] font-display font-semibold text-foreground pr-10">
          {t("settings.voice.title")}
        </h1>
      </div>

      <div className="flex-1 overflow-y-auto px-5 pb-12 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center pt-12">
            <Loader2 className="w-6 h-6 animate-spin text-cyan-300/70" />
          </div>
        ) : (
          <>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="text-[12px] font-body text-foreground/60 leading-relaxed"
            >
              {t("settings.voice.intro")}
            </motion.p>
            {/* Default option */}
            <motion.button
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              type="button"
              onClick={() => handleSelect(null)}
              disabled={isSaving}
              aria-pressed={selectedId === null}
              className="w-full py-4 px-4 rounded-2xl text-left flex items-center justify-between gap-3 transition-all duration-200"
              style={{
                background: (selectedId === null) ? "hsl(187 72% 50% / 0.12)" : "hsl(222 30% 11%)",
                border: (selectedId === null)
                  ? "1px solid hsl(187 72% 50% / 0.35)"
                  : "1px solid hsl(222 18% 22% / 0.6)",
                boxShadow: (selectedId === null) ? "0 0 18px hsl(187 72% 50% / 0.10)" : "none",
              }}
            >
              <div>
                <p className="text-[13px] font-display font-semibold text-foreground">
                  {t("settings.voice.default")}
                </p>
                <p className="text-[11px] text-foreground/55 mt-0.5">
                  {t("settings.voice.defaultHint")}
                </p>
              </div>
              {selectedId === null && <Check size={16} className="text-cyan-300" />}
            </motion.button>

            {/* Voice grid */}
            <div className="grid grid-cols-1 gap-3">
              {(avatarGender ? getVoicesByGender(avatarGender) : VOICE_LIBRARY).map((v, i) => {
                const isSelected = selectedId === v.id;
                const state = rowState[v.id] ?? "idle";
                return (
                  <motion.div
                    key={v.id}
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: 0.05 + i * 0.04 }}
                    className="w-full py-3.5 px-4 rounded-2xl flex items-center justify-between gap-3"
                    style={{
                      background: isSelected ? "hsl(187 72% 50% / 0.12)" : "hsl(222 30% 11%)",
                      border: isSelected
                        ? "1px solid hsl(187 72% 50% / 0.35)"
                        : "1px solid hsl(222 18% 22% / 0.6)",
                      boxShadow: isSelected ? "0 0 18px hsl(187 72% 50% / 0.10)" : "none",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => handleSelect(v.id)}
                      disabled={isSaving}
                      aria-pressed={isSelected}
                      className="flex-1 text-left"
                    >
                      <p className="text-[13px] font-display font-semibold text-foreground">
                        {v.displayName}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePreview(v.id)}
                      disabled={state === "loading-preview"}
                      className="w-8 h-8 rounded-full flex items-center justify-center disabled:opacity-50"
                      style={{
                        background: "hsl(187 72% 50% / 0.10)",
                        border: "1px solid hsl(187 72% 50% / 0.25)",
                      }}
                      aria-label={t("settings.voice.preview")}
                    >
                      {state === "loading-preview"
                        ? <Loader2 size={13} className="animate-spin text-cyan-300" />
                        : <Play size={12} className="text-cyan-300 ml-0.5" />}
                    </button>
                    {isSelected && <Check size={16} className="text-cyan-300" />}
                  </motion.div>
                );
              })}
            </div>

            {!avatarGender && (
              <p className="text-[11px] text-foreground/45 text-center pt-4">
                {t("settings.voice.noAvatarHint")}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SettingsVoice;
